import { SECURE_AUTH_EVENT_IDS } from './consts';
import { IAuthenticationEvent } from '../models/common-models';

interface AuthEvent {
  eventId: string;
  name?: string;
  date?: string;
}

/**
 * Decodes a JWT token and returns the complete payload
 * @param token - The JWT token string
 * @returns The decoded payload object or null if decoding fails
 */
export const decodeJWTPayload = (token: string): Record<string, unknown> | null => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Split JWT into header.payload.signature
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    const payloadPart = parts[0];
    if (!payloadPart) {
      return null;
    }

    // Add padding if needed for base64 decoding
    const paddingNeeded = (4 - (payloadPart.length % 4)) % 4;
    const paddedPayload = payloadPart.padEnd(payloadPart.length + paddingNeeded, '=');

    // Decode base64 JWT payload
    const decoded = atob(paddedPayload);

    // Parse JSON payload
    const parsedPayload = JSON.parse(decoded);

    return parsedPayload;
  } catch (error) {
    return null;
  }
};

/**
 * Extracts a specific property from JWT payload
 * @param token - The JWT token string
 * @param property - The property to extract from the payload
 * @returns The value of the property or null if not found
 */
export const getJWTProperty = <T = unknown>(token: string, property: string): T | null => {
  try {
    const payload = decodeJWTPayload(token);
    if (!payload) {
      return null;
    }

    const result = payload[property] ?? null;

    return result as T | null;
  } catch (error) {
    return null;
  }
};

/**
 * Gets the 'v' property (timestamp) from JWT token payload
 * @param token - The JWT token to decode
 * @returns The timestamp value or null if not found
 */
export const getTimestampFromJWT = (token: string): string | null => {
  return getJWTProperty<string>(token, 'v');
};

/**
 * Determines whether authentication activity should be fetched based on current conditions
 * @param jwt - The JWT token (nullable)
 * @param isScanToPayEnabled - Whether scan-to-pay feature is enabled
 * @param isUserInStore - Whether the user is currently in store
 * @returns True if auth activity should be fetched, false otherwise
 */
export const shouldFetchAuthActivity = (
  jwt: string | null,
  isScanToPayEnabled: boolean,
  isUserInStore: boolean
): boolean => {
  return !!(jwt && isScanToPayEnabled && isUserInStore);
};

/**
 * Processes authentication events to find a matching event based on JWT timestamp
 * @param allEvents - Array of all authentication events
 * @param decodedJWT - The decoded JWT timestamp as a string
 * @param authEventFilterHours - Number of hours to filter events within
 * @returns The matching authentication event or null if not found
 */
export const processAuthEvents = async (
  allEvents: AuthEvent[],
  decodedJWT: string,
  authEventFilterHours: number
): Promise<IAuthenticationEvent | null> => {
  const now = new Date().getTime();
  const authEventCutoffTime = now - (authEventFilterHours * 60 * 60 * 1000);

  // Filter events within the configured time window
  const recentEvents = filterRecentEvents(allEvents, authEventCutoffTime);

  // Filter for valid auth events
  const validAuthEvents = filterValidAuthEvents(recentEvents);

  if (validAuthEvents && Array.isArray(validAuthEvents)) {
    return findMatchingEvent(validAuthEvents, decodedJWT);
  }

  return null;
};

/**
 * Filters events within the specified time window
 * @param allEvents - Array of all authentication events
 * @param authEventCutoffTime - The cutoff timestamp in milliseconds
 * @returns Array of events within the time window
 */
export const filterRecentEvents = (allEvents: AuthEvent[], authEventCutoffTime: number): AuthEvent[] => {
  const firstOldEventIndex = allEvents.findIndex((event) => {
    if (event.date) {
      const eventTimestamp = new Date(event.date).getTime();
      return eventTimestamp < authEventCutoffTime;
    }
    return false;
  });

  return firstOldEventIndex === -1
    ? allEvents
    : allEvents.slice(0, firstOldEventIndex);
};

/**
 * Filters for valid authentication events based on secure event IDs
 * @param events - Array of authentication events to filter
 * @returns Array of valid authentication events
 */
export const filterValidAuthEvents = (events: AuthEvent[]): AuthEvent[] => {
  return events.filter((event) => SECURE_AUTH_EVENT_IDS.includes(event.eventId));
};

/**
 * Finds a matching event based on JWT timestamp with tolerance
 * @param validAuthEvents - Array of valid authentication events
 * @param decodedJWT - The decoded JWT timestamp as a string
 * @returns The matching authentication event or null if not found
 */
export const findMatchingEvent = (validAuthEvents: AuthEvent[], decodedJWT: string): IAuthenticationEvent | null => {
  const jwtTimestamp = parseInt(decodedJWT, 10);
  let matchedEvent: IAuthenticationEvent | null = null;

  validAuthEvents.forEach((event) => {
    if (event.date) {
      const eventTimestamp = new Date(event.date).getTime();

      const timeDifference = Math.abs(jwtTimestamp - eventTimestamp);

      // Check if timestamps match within 1000ms tolerance
      if (timeDifference <= 1000) {
        matchedEvent = event as IAuthenticationEvent;
      }
    }
  });

  return matchedEvent;
};
